import { P2GBox, P2GContainer, P2GGrid, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useNavigate, createSearchParams, useSearchParams } from 'react-router-dom';
import useBasket from '~/hooks/use-basket';
import useSavedAddresses from '~/hooks/use-saved-addresses';
import { createBasketItem } from '~/lib/basket';
import { GenerateWorkflow } from '~/lib/workflow/generate-workflow';
import { BASKETITEMID } from '~/lib/getCurrentItemId';
import useQuotes from '~/hooks/use-quotes';
import { parseParcelsUrlString } from '~/lib/utils/parse-parcels-url-string';
import { CountrySelect } from '~/components/country-select/country-select';
import Loading from '~/components/core/progress/loading';
import Quotes from '~/components/quote-results/quotes';
import NoResults from '~/components/core/switcher/message/no-results';
import InvalidQuote from '~/components/core/switcher/message/invalid-quote';
import { Account, BasketMeta, BasketQuote, QuoteAddress, QuoteRequirements } from '~/lib/types/basket-types';
import { publishTags } from '~/lib/metrics';
import { useAuth } from 'react-oidc-context';
import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import { SavedAddress } from '~/lib/types/edge-api-types';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { doesQuoteForceCollectionAddressSelection, quoteToAnalyticsEvent } from '~/lib/quotes';
import useParseAddress from '~/hooks/use-parse-address';
import { doCountriesEqual } from '~/lib/utils/country';
import { BasketContext } from '~/providers/basketContext';

import { useRequirementsApi } from '~/hooks/use-requirements-api';

export function Component() {
  const basket = useBasket();
  const { dispatch } = useContext(BasketContext);
  const auth = useAuth();
  const navigate = useNavigate();
  const isXs = useP2GMediaQueryOnly('xs');
  const [searchParams, setSearchParams] = useSearchParams();
  const [quoteIsInvalid, setQuoteIsInvalid] = useState(false);
  const searchParamEntries = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);
  const creating = useRef(false);
  const { track } = useAnalytics();
  const { fetchRequirements } = useRequirementsApi();
  const [quoteRequirements, setQuoteRequirements] = useState<QuoteRequirements | undefined>();
  const [requirementsLoading, setRequirementsLoading] = useState(false);

  const parsed = useParseAddress({ query: searchParamEntries.query });

  const quoteOptions = useMemo(() => {
    const {
      cp: originPostcode,
      col: originCountry,
      dest: destinationCountry,
      dp: destinationPostcode,
      p: parcelsText,
      v: parcelValue,
      slug
    } = searchParamEntries;

    return {
      origin: originCountry || 'GBR',
      originPostcode: originPostcode || undefined,
      parcels: parseParcelsUrlString(parcelsText, false, originCountry || 'GBR'),
      destination: parsed.parsed?.countryIso || destinationCountry || 'GBR',
      destinationPostcode: parsed.parsed?.postcode || destinationPostcode || undefined,
      slug: quoteIsInvalid ? undefined : slug,
      value: parcelValue ? parseFloat(parcelValue) : undefined
    };
  }, [searchParamEntries, parsed.parsed?.countryIso, parsed.parsed?.postcode, quoteIsInvalid]);

  const savedAddress = useSavedAddresses(quoteOptions.origin, '');
  const quotes = useQuotes(quoteOptions);

  useEffect(() => {
    let cancelled = false;

    async function loadRequirements() {
      if (!quoteOptions.slug) {
        return;
      }

      setRequirementsLoading(true);

      try {
        const result = await fetchRequirements(quoteOptions.slug, quoteOptions.origin, quoteOptions.destination);

        if (cancelled) {
          return;
        }

        if (result.data) {
          setQuoteRequirements(result.data);
          dispatch({ type: 'UPDATE_REQUIREMENTS', requirements: result.data });
        } else {
          setQuoteRequirements(undefined);
        }
      } finally {
        if (!cancelled) {
          setRequirementsLoading(false);
        }
      }
    }

    loadRequirements();

    return () => {
      cancelled = true;
    };
  }, [quoteOptions.slug, quoteOptions.origin, quoteOptions.destination, fetchRequirements, dispatch]);

  const fromBasket: { account: Account; origin: SavedAddress } | undefined = useMemo(() => {
    if (!basket?.items) {
      return;
    }

    for (const key in basket.items) {
      const item = basket.items[key];
      const meta = item?.sections[BasketSections.META]?.payload as BasketMeta | undefined;
      if (meta?.completed === true) {
        const account = item.sections[BasketSections.ACCOUNT]?.payload as Account | undefined;
        const origin = item.sections[BasketSections.ORIGIN]?.payload as QuoteAddress | undefined;
        if (account && origin && origin.country === quoteOptions.origin) {
          const address: SavedAddress = {
            county: origin.county,
            postcode: origin.postcode,
            town: origin.town,
            email: origin.email,
            name: origin.contactname,
            organisation: origin.organisation,
            phone: origin.phone,
            property: origin.property,
            street: origin.street,
            meta: origin.metadata
          };
          return { account, origin: address };
        }
      }
    }
  }, [basket.items, quoteOptions.origin]);

  const create = useCallback(
    async (quote: BasketQuote, extras: string[]) => {
      const hasParsed = searchParamEntries.query && parsed.parsed;
      // Need to check requirements, but we'll pick this up in the next phase.
      const isInternational = !doCountriesEqual(
        searchParamEntries.col,
        parsed.parsed?.countryIso || searchParamEntries.dest
      );
      const action = auth.isAuthenticated && hasParsed && savedAddress ? 'drop-in' : null;
      const wf = GenerateWorkflow(isXs, isInternational, quote, action);
      const origin = savedAddress?.defaultAddress ?? fromBasket?.origin;
      const strictOrigin = doesQuoteForceCollectionAddressSelection(quote) && !origin?.meta?.deliveryPointSuffix;

      const item = createBasketItem(
        savedAddress?.account ?? fromBasket?.account,
        strictOrigin ? undefined : savedAddress?.defaultAddress ?? fromBasket?.origin,
        quote,
        strictOrigin && origin?.postcode ? { ...searchParamEntries, cp: origin.postcode } : searchParamEntries,
        extras,
        fromBasket !== undefined,
        isInternational &&
          !!quoteRequirements &&
          (quoteRequirements[RequirementTypes.PARCEL_CONTENTS]?.rules?.some((x) => x.required) ||
            quoteRequirements[RequirementTypes.CUSTOMS]?.rules?.some((x) => x.required)),
        parsed.parsed
      );

      const id = await basket.add(item);

      publishTags({
        origin: quoteOptions.origin,
        destination: quoteOptions.destination,
        lane: `${quoteOptions.origin}-${quoteOptions.destination}`,
        service: quote.service.slug
      });

      try {
        track({
          type: 'workflow_screen_view',
          payload: {
            page: `checkout/new`,
            origin: quoteOptions.origin,
            destination: quoteOptions.destination
          }
        });
        track({
          type: 'view_item',
          payload: {
            ecommerce: {
              item_list_name: quotes.id,
              item_list_id: quotes.id,
              items: [quoteToAnalyticsEvent(quote, extras)]
            }
          }
        });
      } catch (e) {
        console.error('Error tracking view_item', e);
      }

      try {
        if (localStorage) {
          localStorage.setItem('last_service', quote.service.slug);
          localStorage.setItem('last_courier', quote.service.courierSlug);
        }
      } catch (e) {
        console.warn('Failed to save last used service', e);
      }

      basket.change(id, action || null);

      navigate(
        {
          pathname: `../${wf.screens[0].path}`,
          search: createSearchParams({
            [BASKETITEMID]: id,
            quoteslug: quote.service.slug,
            mdd: '0',
            dir: '1',
            action: action || ''
          }).toString()
        },
        { relative: 'path', replace: true }
      );
    },
    [
      searchParamEntries,
      auth.isAuthenticated,
      isXs,
      savedAddress?.account,
      savedAddress?.defaultAddress,
      fromBasket,
      parsed.parsed,
      basket,
      quoteOptions.origin,
      quoteOptions.destination,
      navigate,
      track,
      quotes.id,
      quoteRequirements
    ]
  );

  useEffect(() => {
    async function init() {
      if (creating.current) {
        return;
      }
      if (!quoteRequirements) {
        return;
      }
      creating.current = true;
      await create(quotes.quotes[0], (searchParamEntries.u || '').split(','));
      creating.current = false;
    }

    if (!searchParamEntries.query && !searchParamEntries.slug) {
      window.location.href = '/';
      return;
    }

    if (searchParamEntries?.col && (searchParamEntries?.dest || searchParamEntries?.query)) {
      if (
        quotes.loading ||
        basket.loading ||
        quoteIsInvalid ||
        savedAddress.loading ||
        auth.isLoading ||
        parsed.parsing ||
        requirementsLoading
      ) {
        return;
      }

      // No Quote, show the alt services view with the quotes.
      if (quotes.quotes.length === 0 || !searchParamEntries.slug) {
        setQuoteIsInvalid(true);
        return;
      }

      init();
    }
  }, [
    basket,
    searchParamEntries,
    quotes.loading,
    quotes.quotes,
    quoteIsInvalid,
    create,
    savedAddress.loading,
    auth.isLoading,
    parsed.parsing,
    requirementsLoading
  ]);

  if ((!searchParamEntries.dest && !searchParamEntries.query) || !searchParamEntries.col) {
    return <CountrySelect searchParams={searchParamEntries} setSearchParams={setSearchParams} />;
  }

  // Alt Services, no quote found-ish
  if (!quotes.loading && quoteIsInvalid) {
    return (
      <P2GContainer maxWidth="lg">
        <P2GBox>
          {quotes.quotes.length > 0 ? (
            <P2GGrid container direction="column" spacing={2} dataTestId="service-switcher-error">
              {searchParamEntries.slug !== undefined && (
                <P2GGrid item>
                  <InvalidQuote onClose={() => {}} />
                </P2GGrid>
              )}
              <P2GGrid item>
                <Quotes
                  quoteId={quotes.id}
                  hideFilter={true}
                  quotes={quotes.quotes}
                  onSelect={(_, q, e) => create(q, e)}
                  loading={false}
                />
              </P2GGrid>
            </P2GGrid>
          ) : (
            <NoResults onClose={() => {}} />
          )}
        </P2GBox>
      </P2GContainer>
    );
  }

  return <Loading />;
}

Component.displayName = 'New Basket Item Page';
